// category4-question3.component.ts
import { CommonModule } from '@angular/common';
import {
  ChangeDetectorRef,
  Component,
  inject,
  OnDestroy,
  OnInit,
} from '@angular/core';
import {
  AbstractControl,
  FormArray,
  FormBuilder,
  FormControl,
  FormGroup,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { InputComponent } from 'src/app/components/shared/input';
import { CategoryService } from 'src/app/services/category';
import { LocaleService } from 'src/app/services/locale';
import { PlusComponent } from '../../../../icons/plus';
import { DropdownComponent } from '../../../../shared/dropdown';
import { TrashComponent } from '../../../../icons/trash';
import { YearPickerComponent } from '../../../../shared/year-picker';
import { debounceTime, Subscription } from 'rxjs';

interface SolidWasteEntry {
  mainCategory: string;
  material_treatment: string;
  quantity: number;
  unit: string;
  period: string;
}

interface MaterialTreatment {
  id: string;
  label: string;
  itaLabel: string;
}

interface MainCategory {
  name: string;
  itaName: string;
  material_treatment: MaterialTreatment[];
}

interface WasteWaterEntry {
  amount: number;
  currency: string;
  period: string;
}

@Component({
  selector: 'app-category4-question3',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    InputComponent,
    PlusComponent,
    DropdownComponent,
    TrashComponent,
    YearPickerComponent,
  ],
  template: `
    <!-- category4-question3.component.html -->
    <div class="text-[12px] text-[#616161] mt-1">
      {{ strings[language].answer }}
    </div>
    <div class="container mx-auto">
      <!-- Yes / No toggle -->
      <div class="flex flex-wrap gap-4 sm:gap-6 mt-4">
        <div
          (click)="toggleUsage(true)"
          class="cursor-pointer flex gap-2 items-center"
        >
          <div
            [class.bg-green-950]="usage"
            class="w-4 h-4 border rounded-full border-gray-400 flex-shrink-0"
          ></div>
          <span>{{ strings[language].choice_yes }}</span>
        </div>
        <div
          (click)="toggleUsage(false)"
          class="cursor-pointer flex gap-2 items-center"
        >
          <div
            [class.bg-green-950]="!usage"
            class="w-4 h-4 border rounded-full border-gray-400 flex-shrink-0"
          ></div>
          <span>{{ strings[language].choice_no }}</span>
        </div>
      </div>

      <!-- Waste Type Selection (only shown if usage is true) -->
      <div *ngIf="usage" class="mt-6">
        <div class="font-medium mb-2">
          {{
            language === 'english'
              ? 'Select waste type'
              : 'Seleziona il tipo di rifiuto'
          }}
          :
        </div>
        <div class="flex flex-wrap gap-4 sm:gap-6">
          <div
            (click)="onWasteTypeChange('solid')"
            class="cursor-pointer flex gap-2 items-center"
          >
            <div
              [class.bg-green-950]="wasteType === 'solid'"
              class="w-4 h-4 border rounded-full border-gray-400 flex-shrink-0"
            ></div>
            <span>{{ strings[language].solid_waste_option }}</span>
          </div>
          <div
            (click)="onWasteTypeChange('wastewater')"
            class="cursor-pointer flex gap-2 items-center"
          >
            <div
              [class.bg-green-950]="wasteType === 'wastewater'"
              class="w-4 h-4 border rounded-full border-gray-400 flex-shrink-0"
            ></div>
            <span>{{ strings[language].wastewater_option }}</span>
          </div>
        </div>
      </div>

      <!-- Help boxes -->
      <div class="mt-6 space-y-4">
        <div class="bg-[#043B22]/5 p-4 rounded-lg border" *ngIf="usage">
          <div class="font-medium">
            {{ strings[language].help_text1 }}
          </div>
        </div>
      </div>

      <div
        *ngIf="savedRowsExceededLimit"
        class="mt-4 rounded-lg border border-amber-200 bg-amber-50 p-3 text-sm text-amber-800"
      >
        {{
          language === 'english'
            ? 'This saved question has too many rows to open safely in Chrome, so only the first rows are shown. The original saved data has not been overwritten.'
            : 'Questa domanda salvata contiene troppe righe per aprirla in sicurezza su Chrome, quindi vengono mostrate solo le prime righe. I dati salvati originali non sono stati sovrascritti.'
        }}
      </div>

      <!-- Solid Waste Form (shown only when selected) -->
      <div *ngIf="usage && wasteType === 'solid'" class="mt-6">
        <div class="flex justify-between items-center mb-4">
          <h3 class="text-lg font-semibold">
            {{ strings[language].solid_waste_option }}
          </h3>

          <div
            (click)="addSolidWasteRow()"
            class="bg-[#F8F9FA] px-3 py-2 rounded text-white text-md cursor-pointer"
          >
            <app-plus></app-plus>
          </div>
        </div>

        <form [formGroup]="form">
          <div class="space-y-4" formArrayName="solidWaste">
            <div
              *ngFor="let row of solidWaste.controls; let i = index"
              [formGroupName]="i"
              class="border rounded-lg p-4 bg-white shadow-sm"
            >
              <div class="flex justify-end">
                <button
                  type="button"
                  (click)="removeSolidWasteRow(i)"
                  class="text-red-500 hover:text-red-700"
                >
                  <app-trash></app-trash>
                </button>
              </div>

              <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
                <!-- Category -->
                <div>
                  <label class="block text-sm font-medium mb-1">{{
                    strings[language].category_label
                  }}</label>
                  <app-dropdown
                    placeholder="{{ strings[language].select_cat_label }}"
                    [options]="exiobaseMainCategories"
                    [control]="getFormControl(row, 'mainCategory')"
                    (change)="onCategoryChange(row, $event)"
                  ></app-dropdown>
                </div>

                <!-- Material and Treatment -->
                <div>
                  <label class="block text-sm font-medium mb-1">{{
                    strings[language].waste_type_label
                  }}</label>
                  <div
                    [style.pointer-events]="
                      !row.get('mainCategory')?.value ? 'none' : 'auto'
                    "
                    [style.opacity]="
                      !row.get('mainCategory')?.value ? '0.5' : '1'
                    "
                  >
                    <app-dropdown
                      placeholder="{{
                        strings[language].Select_material_and_treatment
                      }}"
                      [options]="
                        getSubcategories(row.get('mainCategory')?.value)
                      "
                      [control]="getFormControl(row, 'material_treatment')"
                    ></app-dropdown>
                  </div>
                </div>

                <!-- Quantity -->
                <div>
                  <label class="block text-sm font-medium mb-1">{{
                    strings[language].quantity_label
                  }}</label>
                  <app-input
                    type="number"
                    [dollar]="false"
                    unit=""
                    placeholder="0.00"
                    [control]="getFormControl(row, 'quantity')"
                  ></app-input>
                </div>

                <!-- Unit -->
                <div>
                  <label class="block text-sm font-medium mb-1">{{
                    strings[language].unit_label
                  }}</label>
                  <app-dropdown
                    [options]="unitOptions"
                    [control]="getFormControl(row, 'unit')"
                  ></app-dropdown>
                </div>

                <!-- Period -->
                <div>
                  <label class="block text-sm font-medium mb-1">{{
                    strings[language].period_label
                  }}</label>
                  <app-year-picker
                    [control]="getFormControl(row, 'period')"
                  ></app-year-picker>
                </div>
              </div>
            </div>
          </div>
        </form>
      </div>

      <!-- Wastewater Form (shown only when selected) -->
      <div *ngIf="usage && wasteType === 'wastewater'" class="mt-6">
        <div class="bg-[#F8F9FA] flex p-4 border-y border-[#DEE2E6] gap-5">
          <div class="text-[#6C757D] font-bold w-1/3">
            {{ strings[language].amount_label }}
          </div>
          <div class="text-[#6C757D] font-bold w-1/3">
            {{ strings[language].currency_label }}
          </div>
          <div class="text-[#6C757D] font-bold w-1/3">
            {{ strings[language].period_label }}
          </div>
          <div
            (click)="addWastewaterRow()"
            class="w-1/8 flex justify-center cursor-pointer align-middle items-center"
          >
            <app-plus></app-plus>
          </div>
        </div>

        <form [formGroup]="form">
          <div formArrayName="wastewater">
            <div
              *ngFor="let row of wastewaterArray.controls; let i = index"
              [formGroupName]="i"
              class="flex py-4 px-1 border-y border-[#DEE2E6] gap-5"
            >
              <div class="w-1/3">
                <app-input
                  type="number"
                  [dollar]="true"
                  placeholder="0.00"
                  [control]="getWastewaterControl(i, 'amount')"
                >
                </app-input>
              </div>

              <div class="w-1/3">
                <app-dropdown
                  [options]="currencyOptions"
                  [control]="getWastewaterControl(i, 'currency')"
                ></app-dropdown>
              </div>

              <div class="w-1/3">
                <app-year-picker
                  [control]="getWastewaterControl(i, 'period')"
                ></app-year-picker>
              </div>

              <div
                (click)="removeWastewaterRow(i)"
                class="w-1/9 flex justify-start align-middle items-center cursor-pointer"
              >
                <app-trash></app-trash>
              </div>
            </div>
          </div>
        </form>

        <div class="bg-[#043B22]/5 p-4 rounded-lg mt-4">
          <p class="text-gray-600">
            {{ strings[language].wastewater_instructions }}
          </p>
        </div>
      </div>
    </div>
  `,
})
export class Category4Question3Component implements OnInit, OnDestroy {
  language = inject(LocaleService).getLanguage();
  categoryService = inject(CategoryService);
  fb = inject(FormBuilder);
  cdr = inject(ChangeDetectorRef);
  isShowDiv: boolean = false;
  form!: FormGroup;
  usage = false;
  wasteType: 'solid' | 'wastewater' | null = null;
  savedRowsExceededLimit = false;
  private formSubscription?: Subscription;
  private readonly maxRenderableRows = 250;

  PRODUCT_CATEGORIES: MainCategory[] = [
    {
      name: 'FOOD & ORGANIC',
      itaName: 'ALIMENTARI E ORGANICI',
      material_treatment: [
        {
          id: 'waste-type_organic_food_and_drink-disposal_method_landfill',
          label: 'Organic food and drink waste via landfill',
          itaLabel: 'Rifiuti organici alimentari e bevande via discarica',
        },
        {
          id: 'waste-type_organic_food_and_drink-disposal_method_combustion',
          label: 'Organic food and drink waste via combustion',
          itaLabel: 'Rifiuti organici alimentari e bevande via combustione',
        },
        {
          id: 'waste-type_organic_food_and_drink-disposal_method_composting',
          label: 'Organic food and drink waste via composting',
          itaLabel: 'Rifiuti organici alimentari e bevande via compostaggio',
        },
        {
          id: 'waste-type_organic_food_and_drink-disposal_method_anaerobic_digestion',
          label: 'Organic food and drink waste via anaerobic digestion',
          itaLabel:
            'Rifiuti organici alimentari e bevande via digestione anaerobica',
        },
        {
          id: 'waste-type_organic_garden-disposal_method_landfill',
          label: 'Organic garden waste via landfill',
          itaLabel: 'Rifiuti organici da giardino via discarica',
        },
        {
          id: 'waste-type_organic_garden-disposal_method_combustion',
          label: 'Organic garden waste via combustion',
          itaLabel: 'Rifiuti organici da giardino via combustione',
        },
        {
          id: 'waste-type_organic_garden-disposal_method_composting',
          label: 'Organic garden waste via composting',
          itaLabel: 'Rifiuti organici da giardino via compostaggio',
        },
        {
          id: 'waste-type_organic_garden-disposal_method_anaerobic_digestion',
          label: 'Organic garden waste via anaerobic digestion',
          itaLabel: 'Rifiuti organici da giardino via digestione anaerobica',
        },
      ],
    },
    {
      name: 'MIXED RESIDUAL',
      itaName: 'RESIDUI MISTI',
      material_treatment: [
        {
          id: 'waste-type_household_residual_waste-disposal_method_landfill',
          label: 'Household residual waste via landfill',
          itaLabel: 'Rifiuti domestici residui via discarica',
        },
        {
          id: 'waste-type_household_residual_waste-disposal_method_combustion',
          label: 'Household residual waste via combustion',
          itaLabel: 'Rifiuti domestici residui via combustione',
        },
        {
          id: 'waste-type_household_residual_waste-disposal_method_composting',
          label: 'Household residual waste via composting',
          itaLabel: 'Rifiuti domestici residui via compostaggio',
        },
        {
          id: 'waste-type_household_residual_waste-disposal_method_anaerobic_digestion',
          label: 'Household residual waste via anaerobic digestion',
          itaLabel: 'Rifiuti domestici residui via digestione anaerobica',
        },
        {
          id: 'waste-type_commercial_and_industrial-disposal_method_landfill',
          label: 'Commercial and industrial waste via landfill',
          itaLabel: 'Rifiuti commerciali e industriali via discarica',
        },
        {
          id: 'waste-type_commercial_and_industrial-disposal_method_combustion',
          label: 'Commercial and industrial waste via combustion',
          itaLabel: 'Rifiuti commerciali e industriali via combustione',
        },
        {
          id: 'waste-type_commercial_and_industrial-disposal_method_composting',
          label: 'Commercial and industrial waste via composting',
          itaLabel: 'Rifiuti commerciali e industriali via compostaggio',
        },
        {
          id: 'waste-type_commercial_and_industrial-disposal_method_anaerobic_digestion',
          label: 'Commercial and industrial waste via anaerobic digestion',
          itaLabel:
            'Rifiuti commerciali e industriali via digestione anaerobica',
        },
        {
          id: 'waste-type_commercial_and_industrial-disposal_method_closed_loop',
          label: 'Commercial and industrial waste via closed-loop recycling',
          itaLabel:
            'Rifiuti commerciali e industriali via riciclo a ciclo chiuso',
        },
      ],
    },
    {
      name: 'PAPER / CARDBOARD',
      itaName: 'CARTA / CARTONE',
      material_treatment: [
        {
          id: 'waste-type_paper-disposal_method_landfill',
          label: 'Paper and cardboard waste via landfill',
          itaLabel: 'Rifiuti di carta e cartone via discarica',
        },
        {
          id: 'waste-type_paper-disposal_method_combustion_energy_recovery',
          label: 'Paper and cardboard waste via combustion',
          itaLabel: 'Rifiuti di carta e cartone via combustione',
        },
        {
          id: 'waste-type_paper-disposal_method_composting',
          label: 'Paper and cardboard waste via composting',
          itaLabel: 'Rifiuti di carta e cartone via compostaggio',
        },
        {
          id: 'waste-type_paper-disposal_method_closed_loop',
          label: 'Paper and cardboard waste via closed-loop recycling',
          itaLabel: 'Rifiuti di carta e cartone via riciclo a ciclo chiuso',
        },
      ],
    },
    {
      name: 'PLASTICS',
      itaName: 'PLASTICA',
      material_treatment: [
        {
          id: 'waste-type_plastics-disposal_method_landfill',
          label: 'Plastic waste via landfill',
          itaLabel: 'Rifiuti di plastica via discarica',
        },
        {
          id: 'waste-type_plastics-disposal_method_combustion',
          label: 'Plastic waste via combustion',
          itaLabel: 'Rifiuti di plastica via combustione',
        },
        {
          id: 'waste-type_plastics-disposal_method_closed_loop',
          label: 'Plastic waste via closed-loop recycling',
          itaLabel: 'Rifiuti di plastica via riciclo a ciclo chiuso',
        },
        {
          id: 'waste-type_plastics-disposal_method_open_loop',
          label: 'Plastic waste via open-loop recycling',
          itaLabel: 'Rifiuti di plastica via riciclo a ciclo aperto',
        },
      ],
    },
    {
      name: 'GLASS',
      itaName: 'VETRO',
      material_treatment: [
        {
          id: 'waste-type_glass-disposal_method_landfill',
          label: 'Glass waste via landfill',
          itaLabel: 'Rifiuti di vetro via discarica',
        },
        {
          id: 'waste-type_glass-disposal_method_combustion',
          label: 'Glass waste via combustion',
          itaLabel: 'Rifiuti di vetro via combustione',
        },
        {
          id: 'waste-type_glass-disposal_method_closed_loop',
          label: 'Glass waste via closed-loop recycling',
          itaLabel: 'Rifiuti di vetro via riciclo a ciclo chiuso',
        },
        {
          id: 'waste-type_glass-disposal_method_open_loop',
          label: 'Glass waste via open-loop recycling',
          itaLabel: 'Rifiuti di vetro via riciclo a ciclo aperto',
        },
      ],
    },
    {
      name: 'METALS (ferrous & non-ferrous mix)',
      itaName: 'METALLI (ferrosi e non ferrosi)',
      material_treatment: [
        {
          id: 'waste-type_metals-disposal_method_landfill',
          label: 'Metal waste via landfill',
          itaLabel: 'Rifiuti metallici via discarica',
        },
        {
          id: 'waste-type_metals-disposal_method_closed_loop',
          label: 'Metal waste via closed-loop recycling',
          itaLabel: 'Rifiuti metallici via riciclo a ciclo chiuso',
        },
      ],
    },
    {
      name: 'WOOD',
      itaName: 'LEGNO',
      material_treatment: [
        {
          id: 'waste-type_wood-disposal_method_landfill',
          label: 'Wood waste via landfill',
          itaLabel: 'Rifiuti di legno via discarica',
        },
        {
          id: 'waste-type_wood-disposal_method_combustion_energy_recovery',
          label: 'Wood waste via combustion (energy recovery)',
          itaLabel: 'Rifiuti di legno via combustione (recupero energetico)',
        },
        {
          id: 'waste-type_wood-disposal_method_composting',
          label: 'Wood waste via composting',
          itaLabel: 'Rifiuti di legno via compostaggio',
        },
        {
          id: 'waste-type_wood-disposal_method_closed_loop',
          label: 'Wood waste via closed-loop recycling',
          itaLabel: 'Rifiuti di legno via riciclo a ciclo chiuso',
        },
      ],
    },
    {
      name: 'TEXTILES / CLOTHING',
      itaName: 'TESSILI / ABBIGLIAMENTO',
      material_treatment: [
        {
          id: 'waste-type_clothing-disposal_method_landfill',
          label: 'Clothing waste via landfill',
          itaLabel: 'Rifiuti tessili via discarica',
        },
        {
          id: 'waste-type_clothing-disposal_method_combustion',
          label: 'Clothing waste via combustion',
          itaLabel: 'Rifiuti tessili via combustione',
        },
        {
          id: 'waste-type_clothing-disposal_method_closed_loop',
          label: 'Clothing waste via closed-loop recycling',
          itaLabel: 'Rifiuti tessili via riciclo a ciclo chiuso',
        },
      ],
    },
    {
      name: 'Construction and demolition',
      itaName: 'RIFIUTI DA COSTRUZIONE E DEMOLIZIONE',
      material_treatment: [
        {
          id: 'waste-type_aggregates-disposal_method_closed_loop',
          label: 'Aggregate waste via closed-loop recycling',
          itaLabel: 'Rifiuti di aggregati via riciclo a ciclo chiuso',
        },
        {
          id: 'waste-type_aggregates-disposal_method_landfill',
          label: 'Aggregate waste via landfill',
          itaLabel: 'Rifiuti di aggregati via discarica',
        },
        {
          id: 'waste-type_aggregates-disposal_method_open_loop',
          label: 'Aggregate waste via open-loop recycling',
          itaLabel: 'Rifiuti di aggregati via riciclo a ciclo aperto',
        },
        {
          id: 'waste-type_asphalt-disposal_method_closed_loop',
          label: 'Asphalt waste via closed-loop recycling',
          itaLabel: 'Rifiuti di asfalto via riciclo a ciclo chiuso',
        },
        {
          id: 'waste-type_asphalt-disposal_method_landfill',
          label: 'Asphalt waste via landfill',
          itaLabel: 'Rifiuti di asfalto via discarica',
        },
        {
          id: 'waste-type_asphalt-disposal_method_open_loop',
          label: 'Asphalt waste via open-loop recycling',
          itaLabel: 'Rifiuti di asfalto via riciclo a ciclo aperto',
        },
        {
          id: 'waste-type_bricks-disposal_method_landfill',
          label: 'Brick waste via landfill',
          itaLabel: 'Rifiuti di mattoni via discarica',
        },
        {
          id: 'waste-type_bricks-disposal_method_open_loop',
          label: 'Brick waste via open-loop recycling',
          itaLabel: 'Rifiuti di mattoni via riciclo a ciclo aperto',
        },
        {
          id: 'waste-type_concrete-disposal_method_closed_loop',
          label: 'Concrete waste via closed-loop recycling',
          itaLabel: 'Rifiuti di calcestruzzo via riciclo a ciclo chiuso',
        },
        {
          id: 'waste-type_concrete-disposal_method_landfill',
          label: 'Concrete waste via landfill',
          itaLabel: 'Rifiuti di calcestruzzo via discarica',
        },
        {
          id: 'waste-type_concrete-disposal_method_open_loop',
          label: 'Concrete waste via open-loop recycling',
          itaLabel: 'Rifiuti di calcestruzzo via riciclo a ciclo aperto',
        },
        {
          id: 'waste-type_construction-disposal_method_closed_loop',
          label: 'Construction waste via closed-loop recycling',
          itaLabel: 'Rifiuti da costruzione via riciclo a ciclo chiuso',
        },
        {
          id: 'waste-type_construction-disposal_method_open_loop',
          label: 'Construction waste via open-loop recycling',
          itaLabel: 'Rifiuti da costruzione via riciclo a ciclo aperto',
        },
        {
          id: 'waste-type_construction-disposal_method_combustion',
          label: 'Construction waste via combustion',
          itaLabel: 'Rifiuti da costruzione via combustione',
        },
        {
          id: 'waste-type_plasterboard-disposal_method_closed_loop',
          label: 'Plasterboard waste via closed-loop recycling',
          itaLabel: 'Rifiuti di cartongesso via riciclo a ciclo chiuso',
        },
        {
          id: 'waste-type_plasterboard-disposal_method_landfill',
          label: 'Plasterboard waste via landfill',
          itaLabel: 'Rifiuti di cartongesso via discarica',
        },
        {
          id: 'waste-type_soils-disposal_method_closed_loop',
          label: 'Soil waste via closed-loop recycling',
          itaLabel: 'Rifiuti di suolo via riciclo a ciclo chiuso',
        },
        {
          id: 'waste-type_soils-disposal_method_landfill',
          label: 'Soil waste via landfill',
          itaLabel: 'Rifiuti di suolo via discarica',
        },
        {
          id: 'waste-type_insulation-disposal_method_closed_loop',
          label: 'Insulation waste via closed-loop recycling',
          itaLabel: 'Rifiuti di isolamento via riciclo a ciclo chiuso',
        },
        {
          id: 'waste-type_insulation-disposal_method_landfill',
          label: 'Insulation waste via landfill',
          itaLabel: 'Rifiuti di isolamento via discarica',
        },
        {
          id: 'waste-type_asbestos-disposal_method_landfill',
          label: 'Asbestos waste via landfill',
          itaLabel: 'Rifiuti di amianto via discarica',
        },
      ],
    },
    {
      name: 'Hazardous / special wastes',
      itaName: 'RIFIUTI PERICOLOSI / SPECIALI',
      material_treatment: [
        {
          id: 'waste-type_batteries-disposal_method_open_loop',
          label: 'Battery waste via open-loop recycling',
          itaLabel: 'Rifiuti di batterie via riciclo a ciclo aperto',
        },
        {
          id: 'waste-type_batteries-disposal_method_landfill',
          label: 'Battery waste via landfill',
          itaLabel: 'Rifiuti di batterie via discarica',
        },
        {
          id: 'waste-type_mineral_oil-disposal_method_closed_loop',
          label: 'Mineral oil waste via closed-loop recycling',
          itaLabel: 'Rifiuti di olio minerale via riciclo a ciclo chiuso',
        },
        {
          id: 'waste-type_mineral_oil-disposal_method_combustion',
          label: 'Mineral oil waste via combustion',
          itaLabel: 'Rifiuti di olio minerale via combustione',
        },
        {
          id: 'waste-type_tires-disposal_method_closed_loop',
          label: 'Tire waste via closed-loop recycling',
          itaLabel: 'Rifiuti di pneumatici via riciclo a ciclo chiuso',
        },
      ],
    },
    {
      name: 'Waste Electrical and Electronic Equipment',
      itaName: 'RIFIUTI ELETTRICI ED ELETTRONICI',
      material_treatment: [
        {
          id: 'waste-type_weee_fridges_and_freezers-disposal_method_open_loop',
          label:
            'Waste electrical and electronic equipment – fridges and freezers via open-loop recycling',
          itaLabel:
            'Rifiuti di apparecchiature elettriche ed elettroniche – frigoriferi e congelatori via riciclo a ciclo aperto',
        },
        {
          id: 'waste-type_weee_fridges_and_freezers-disposal_method_landfill',
          label:
            'Waste electrical and electronic equipment – fridges and freezers via landfill',
          itaLabel:
            'Rifiuti di apparecchiature elettriche ed elettroniche – frigoriferi e congelatori via discarica',
        },
        {
          id: 'waste-type_weee_large-disposal_method_open_loop',
          label:
            'Waste electrical and electronic equipment – large appliances via open-loop recycling',
          itaLabel:
            'Rifiuti di apparecchiature elettriche ed elettroniche – grandi elettrodomestici via riciclo a ciclo aperto',
        },
        {
          id: 'waste-type_weee_large-disposal_method_landfill',
          label:
            'Waste electrical and electronic equipment – large appliances via landfill',
          itaLabel:
            'Rifiuti di apparecchiature elettriche ed elettroniche – grandi elettrodomestici via discarica',
        },
        {
          id: 'waste-type_weee_large-disposal_method_combustion',
          label:
            'Waste electrical and electronic equipment – large appliances via combustion',
          itaLabel:
            'Rifiuti di apparecchiature elettriche ed elettroniche – grandi elettrodomestici via combustione',
        },
        {
          id: 'waste-type_weee_mixed-disposal_method_open_loop',
          label:
            'Waste electrical and electronic equipment – mixed appliances via open-loop recycling',
          itaLabel:
            'Rifiuti di apparecchiature elettriche ed elettroniche – apparecchiature miste via riciclo a ciclo aperto',
        },
        {
          id: 'waste-type_weee_mixed-disposal_method_landfill',
          label:
            'Waste electrical and electronic equipment – mixed appliances via landfill',
          itaLabel:
            'Rifiuti di apparecchiature elettriche ed elettroniche – apparecchiature miste via discarica',
        },
        {
          id: 'waste-type_weee_mixed-disposal_method_combustion',
          label:
            'Waste electrical and electronic equipment – mixed appliances via combustion',
          itaLabel:
            'Rifiuti di apparecchiature elettriche ed elettroniche – apparecchiature miste via combustione',
        },
        {
          id: 'waste-type_weee_small-disposal_method_open_loop',
          label:
            'Waste electrical and electronic equipment – small appliances via open-loop recycling',
          itaLabel:
            'Rifiuti di apparecchiature elettriche ed elettroniche – piccoli elettrodomestici via riciclo a ciclo aperto',
        },
        {
          id: 'waste-type_weee_small-disposal_method_landfill',
          label:
            'Waste electrical and electronic equipment – small appliances via landfill',
          itaLabel:
            'Rifiuti di apparecchiature elettriche ed elettroniche – piccoli elettrodomestici via discarica',
        },
        {
          id: 'waste-type_weee_small-disposal_method_combustion',
          label:
            'Waste electrical and electronic equipment – small appliances via combustion',
          itaLabel:
            'Rifiuti di apparecchiature elettriche ed elettroniche – piccoli elettrodomestici via combustione',
        },
      ],
    },
  ];

  strings: any = {
    english: {
      Select_material_and_treatment: 'Select material and treatment',
      select_cat_label: 'Select category',
      answer: 'Please fill in the required data if yes to proceed.',
      choice_yes: 'Yes',
      choice_no: 'No',
      add_row: 'Add row',
      remove: 'Remove',
      category_label: 'Category',
      waste_type_label: 'Material and treatment',
      quantity_label: 'Quantity',
      unit_label: 'Unit',
      period_label: 'Period',
      wastewater_heading: 'Wastewater Details',
      amount_label: 'Spend on waste-water treatment',
      currency_label: 'Currency',
      help_text1:
        'Please complete this section only if your organisation has sent solid or liquid waste to an external waste management facility during the reporting period (e.g. for landfill, recycling, incineration, or treatment).',
      help_text2: 'How to complete this section',
      solid_waste_option: 'Solid & sludge waste',
      wastewater_option: 'Waste-water & effluents',
    },
    italian: {
      Select_material_and_treatment: 'Seleziona materiale e trattamento',
      select_cat_label: 'Seleziona categoria',
      answer:
        'Si prega di inserire i dati richiesti se la risposta è sì per procedere.',
      choice_yes: 'Sì',
      choice_no: 'No',
      add_row: 'Aggiungi riga',
      remove: 'Rimuovi',
      category_label: 'Categoria',
      waste_type_label: 'Materiale e trattamento',
      quantity_label: 'Quantità',
      unit_label: 'Unità',
      period_label: 'Periodo',
      wastewater_heading: 'Dettagli acque reflue',
      amount_label: 'Spesa per il trattamento delle acque reflue',
      currency_label: 'Valuta',
      help_text1:
        'Si prega di compilare questa sezione solo se la vostra organizzazione ha inviato rifiuti solidi o liquidi a un impianto esterno di gestione dei rifiuti durante il periodo di rendicontazione (ad es. per discarica, riciclaggio, incenerimento o trattamento).',
      help_text2: 'Come completare questa sezione',
      solid_waste_option: 'Rifiuti solidi e fangosi',
      wastewater_option: 'Acque reflue e effluenti',
    },
  };

  unitOptions = [
    { value: 'kg', label: 'kg' },
    { value: 't', label: 't' },
  ];

  currencyOptions = [
    { value: 'EUR', label: 'EUR' },
    { value: 'USD', label: 'USD' },
  ];

  get solidWaste(): FormArray {
    return this.form.get('solidWaste') as FormArray;
  }

  get wastewater(): FormGroup {
    return this.form.get('wastewater') as FormGroup;
  }

  ngOnInit(): void {
    this.initForm();
    this.loadFromLocalStorage();
  }

  ngOnDestroy(): void {
    this.formSubscription?.unsubscribe();
    this.categoryService.blockNavigation.set(false);
  }

  initForm(): void {
    this.form = this.fb.group({
      wasteType: [null],
      solidWaste: this.fb.array([]),
      wastewater: this.fb.array([]),
    });
  }
  get wastewaterArray(): FormArray {
    return this.form.get('wastewater') as FormArray;
  }

  addWastewaterRow(): void {
    this.wastewaterArray.push(this.createWastewaterGroup());
  }

  removeWastewaterRow(index: number): void {
    this.wastewaterArray.removeAt(index);
    this.saveToLocalStorage();
  }

  createWastewaterGroup(entry?: WasteWaterEntry): FormGroup {
    return this.fb.group({
      amount: [entry?.amount || 0, [Validators.required, Validators.min(0)]],
      currency: [entry?.currency || 'EUR', Validators.required],
      period: [entry?.period || '', Validators.required],
    });
  }
  loadFromLocalStorage(): void {
    const categories = (this.categoryService.categories() as any[]) || [];
    const answers = categories?.[3]?.questions?.[2]?.answers;

    if (Array.isArray(answers)) {
      const solidWasteAnswers = answers.filter((a: any) => a?.mainCategory);
      const wastewaterAnswers = answers.filter(
        (a: any) => a?.amount !== undefined,
      );

      if (solidWasteAnswers.length > 0) {
        this.wasteType = 'solid';
        const renderableAnswers =
          this.limitRenderableAnswers(solidWasteAnswers);
        renderableAnswers.forEach((entry: any) => {
          this.solidWaste.push(this.createSolidWasteGroup(entry));
        });

        setTimeout(() => {
          renderableAnswers.forEach((entry: any, index: number) => {
            if (entry.material_treatment) {
              this.getFormControl(
                this.solidWaste.at(index),
                'material_treatment',
              ).setValue(entry.material_treatment, { emitEvent: false });
            }
          });
          this.cdr.detectChanges();
        }, 0);
      } else if (wastewaterAnswers.length > 0) {
        this.wasteType = 'wastewater';
        this.limitRenderableAnswers(wastewaterAnswers).forEach((entry: any) => {
          this.wastewaterArray.push(this.createWastewaterGroup(entry));
        });
      }

      this.usage = this.wasteType !== null;
      if (this.usage) {
        this.form.get('wasteType')?.setValue(this.wasteType, {
          emitEvent: false,
        });
      }
    }

    this.formSubscription = this.form.valueChanges
      .pipe(debounceTime(150))
      .subscribe(() => {
        this.saveToLocalStorage();
      });
    this.updateBlockNavigation(false);
  }

  toggleUsage(value: boolean): void {
    this.usage = value;
    if (!value) {
      this.savedRowsExceededLimit = false;
      this.wasteType = null;
      this.solidWaste.clear();
      this.wastewater.reset({
        amount: 0,
        currency: 'EUR',
        period: '',
      });
      this.form.get('wasteType')?.reset();
    }
    this.saveToLocalStorage();
  }

  onWasteTypeChange(type: 'solid' | 'wastewater'): void {
    this.savedRowsExceededLimit = false;
    this.wasteType = type;

    if (type === 'solid' && this.solidWaste.length === 0) {
      this.addSolidWasteRow();
    }

    if (type === 'wastewater' && this.wastewaterArray.length === 0) {
      this.addWastewaterRow(); // Add a default row for wastewater
    }

    this.saveToLocalStorage();
  }

  addSolidWasteRow(): void {
    this.isShowDiv = false;
    this.solidWaste.push(this.createSolidWasteGroup());
  }

  removeSolidWasteRow(index: number): void {
    this.solidWaste.removeAt(index);
    this.saveToLocalStorage();
  }

  createSolidWasteGroup(entry?: SolidWasteEntry): FormGroup {
    return this.fb.group({
      mainCategory: [entry?.mainCategory || '', Validators.required],
      material_treatment: [
        entry?.material_treatment || '',
        Validators.required,
      ],
      quantity: [
        entry?.quantity || 0,
        [Validators.required, Validators.min(0)],
      ],
      unit: [entry?.unit || 'kg', Validators.required],
      period: [entry?.period || '', Validators.required],
    });
  }

  getFormControl(row: AbstractControl, name: string): FormControl {
    return row.get(name) as FormControl;
  }
  onCategoryChange(row: AbstractControl, category: any) {
    // Clear the material_treatment value when category changes
    this.getFormControl(row, 'material_treatment').setValue('');
    // row.get('material_treatment')?.reset();
    this.saveToLocalStorage();
  }
  getWastewaterControl(index: number, name: string): FormControl {
    return this.wastewaterArray.at(index).get(name) as FormControl;
  }

  saveToLocalStorage(): void {
    if (this.savedRowsExceededLimit) {
      this.categoryService.blockNavigation.set(false);
      return;
    }

    const categories = (this.categoryService.categories() as any[]) || [];

    let answers: any[] = [];

    if (this.wasteType === 'solid') {
      this.updateBlockNavigation(true);

      answers = this.solidWaste.controls
        .filter((group) => {
          const g = group.value;
          return g.mainCategory && g.material_treatment && g.quantity > 0;
        })
        .map((group) => {
          const g = group.value;
          const selectedMainCategory = g.mainCategory;
          const selectedMaterialTreatment = g.material_treatment;

          let mainCategoryInfo: any = null;
          let materialTreatmentInfo: any = null;

          for (const category of this.PRODUCT_CATEGORIES) {
            if (category.name === selectedMainCategory) {
              mainCategoryInfo = {
                id: category.name,
                valueLabel: category.name, // raw value
                label: category.name, // human-readable
                itaLabel: category.itaName, // Italian
              };

              const foundTreatment = category.material_treatment.find(
                (mt) => mt.id === selectedMaterialTreatment,
              );
              if (foundTreatment) {
                materialTreatmentInfo = {
                  id: foundTreatment.id,
                  valueLabel: foundTreatment.id, // raw value
                  label: foundTreatment.label, // human-readable
                  itaLabel: foundTreatment.itaLabel, // Italian
                };
              }
              break;
            }
          }

          return {
            mainCategory: g.mainCategory,
            main_category_info: mainCategoryInfo,
            material_treatment: g.material_treatment,
            material_treatment_info: materialTreatmentInfo,
            quantity: g.quantity,
            unit: g.unit,
            period: g.period,
          };
        });
    } else if (this.wasteType === 'wastewater') {
      answers = this.wastewaterArray.value.map((item: any) => ({
        ...item,
        FieldLabel: 'Spend on waste-water treatment',
        valueLabel: item.value, // raw value
        label: item.label, // human-readable if available
      }));
    }

    if (categories[3] && categories[3].questions[2]) {
      categories[3].questions[2].answers = answers.length > 0 ? answers : null;
    }

    this.categoryService.persistCategories(categories);
  }

  private limitRenderableAnswers<T>(answers: T[]): T[] {
    if (answers.length <= this.maxRenderableRows) {
      return answers;
    }

    this.savedRowsExceededLimit = true;
    console.warn(
      `Category 4 question 3 has ${answers.length} saved rows; rendering the first ${this.maxRenderableRows} to avoid locking the browser.`,
    );
    return answers.slice(0, this.maxRenderableRows);
  }

  private updateBlockNavigation(markTouched: boolean): void {
    if (this.wasteType !== 'solid' || this.savedRowsExceededLimit) {
      this.categoryService.blockNavigation.set(false);
      return;
    }

    let hasIncomplete = false;
    this.solidWaste.controls.forEach((group) => {
      const g = group.value;
      if (g.mainCategory && !g.material_treatment) {
        if (markTouched) {
          (group as FormGroup).get('material_treatment')?.markAsTouched();
        }
        hasIncomplete = true;
      }
    });
    this.categoryService.blockNavigation.set(hasIncomplete);
  }

  private _mainCategoriesCache: {
    lang: string;
    opts: { value: string; label: string }[];
  } | null = null;

  get exiobaseMainCategories(): { value: string; label: string }[] {
    const lang = this.language;
    if (this._mainCategoriesCache?.lang === lang) {
      return this._mainCategoriesCache.opts;
    }
    const opts = this.PRODUCT_CATEGORIES.map((x) => ({
      value: x.name,
      label: lang === 'english' ? x.name : x.itaName,
    }));
    this._mainCategoriesCache = { lang, opts };
    return opts;
  }

  private subcategoryCache = new Map<
    string,
    { value: string; label: string }[]
  >();

  getSubcategories(mainCat: string) {
    if (!mainCat) return [];
    if (this.subcategoryCache.has(mainCat)) {
      return this.subcategoryCache.get(mainCat)!;
    }
    const cat = this.PRODUCT_CATEGORIES.find((c) => c.name === mainCat);
    const result =
      cat?.material_treatment?.map((sub) => ({
        value: sub.id,
        label: this.language === 'english' ? sub.label : sub.itaLabel,
      })) || [];
    this.subcategoryCache.set(mainCat, result);
    return result;
  }
}
